import Copy from "@/components/copy";
import moment from "moment";
import { type ColumnDef } from "@tanstack/react-table";
import { formatAmount } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useSheetStore } from "@/zustand/sheetStore";
import { Text } from "lucide-react";
import { useFormatAmountWithCurrency } from "@/hooks/use-format-with-currency";

const typeMap = {
  TransferIn: "Deposit",
  TransferOut: "Withdrawal",
  Fee_Consumption: "Fee",
};

export const cardColumns: ColumnDef<CardTransaction>[] = [
  {
    header: "Type",
    cell: ({ row }) => {
      return (
        <div className="break-word w-[200px] text-wrap">
          {row.original.merchantName || typeMap[row.original.type] || "-"}
        </div>
      );
    },
  },

  {
    header: "Merchant",
    cell: ({ row }) => {
      return (
        <div className="break-word w-[200px] text-wrap">
          {row.original.merchantName || "-"}
        </div>
      );
    },
  },
  {
    header: "Amount",
    cell: ({ row }) => {
      const formatAmount = useFormatAmountWithCurrency();
      return <div>{formatAmount(row.original.amount)}</div>;
    },
  },
  {
    header: "Date",
    cell: ({ row }) => (
      <div className="font-semibold">
        {moment(row.original.createTime).format("DD-MM hh:mm A")}
      </div>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
  {
    header: "Action",
    cell: ({ row }) => {
      const { openSheet } = useSheetStore();
      return (
        <div>
          <Button
            className="text-primary-500 flex h-auto items-center gap-1 rounded-full bg-[#ECFAFF] px-4 py-2 text-xs text-[10px] hover:bg-white"
            onClick={() =>
              openSheet("transaction-details", null, {
                transactionData: row.original,
                type: "card",
              })
            }
          >
            <Text className="size-2.5" />
            <span>Details</span>
          </Button>
        </div>
      );
    },
  },
];

export const StatusBadge = ({
  status,
  className,
}: {
  status: CardTransaction["status"];
  className?: string;
}) => {
  const statusConfig = {
    completed: {
      color: "text-[#77FF9E]",
      bgColor: "bg-[#8DFFC073]",
      icon: "●",
    },
    failed: { color: "text-[#FF6E7A]", bgColor: "bg-red-50", icon: "●" },
    rejected: { color: "text-[#FF6E7A]", bgColor: "bg-[#FF6E7A1A]", icon: "●" },
    pending: { color: "text-[#FFE261]", bgColor: "bg-[#FFE2611A]", icon: "●" },
    closed: { color: "text-[#FFE261]", bgColor: "bg-[#FFE2611A]", icon: "●" },
  };

  const config = statusConfig[status];

  return (
    <div
      className={`inline-flex items-center rounded-full px-4 py-2 text-[10px] ${config?.color} ${config?.bgColor} ${className}`}
    >
      <span className="mr-1">{config?.icon}</span>
      <span className="capitalize">{status}</span>
    </div>
  );
};
